import Button from "@mui/material/Button";
import {
  Checkbox,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Menu,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGridPro as DataGrid, useGridApiRef } from "@mui/x-data-grid-pro";
import { useState } from "react";
import Translate from "../../utils/Translate";
import AutocompleteSearchField from "../AutocompleteSearchField";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import ActorAutocompleteOption from "../ActorAutocompleteOption";
import { isFreightPartType } from "../../utils/part";
import DotMenu from "../DotMenu";
import {
  AccountBalance,
  Add,
  AttachMoney,
  Clear,
  Computer,
  Delete,
  Discount,
  Download,
  Edit,
  Email,
  Error,
  MarkEmailRead,
  Money,
  MoreHoriz,
  Numbers,
  Percent,
  Print,
  Visibility,
  VisibilityOff,
  Warning,
} from "@mui/icons-material";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import { IconAndLabelColumnHeader } from "../../utils/dataGrid";
import { canUpdateOrderLine, orderStatus } from "../../utils/order";
import { formatMoney } from "../../utils/formatMoney";
import { DatePicker } from "@mui/x-date-pickers";
import {
  formatDate,
  getCurrentDateMask,
  getCurrentInputFormat,
  isValidDate,
  toSameDateTimeUTC,
} from "../../utils/dateTimeFormat";
import MenuToggleButton from "../MenuToggleButton";
import { getIcon, priority } from "../../utils/priority";
import { documentType, mediaType } from "../../utils/sentCommunication";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { paymentStatus } from "../../utils/payment";
import { accessKeys, hasSomeAccess } from "../../utils/userAccess";

export default function OrderFormAdvanced({
  order,
  orderLines,
  orderCommunications,
  company,
  contacts,
  receiverCompany,
  receiverContacts,
  addresses,
  receiverCompanyAddresses,
  deliveryAgreements,
  paymentAgreements,
  modesOfTransport,
  currencies,
  handleDeliveryAddressSelected,
  companySearch,
  handleCompanySelected,
  handleReceiverCompanySelected,
  handleResetReceiverCompany,
  handleResetInvoiceReference,
  canChangeCompany,
  isExternalDataIdRequired,
  handleContactSelected,
  isServiceAgreement,
  isEPortLicense,
  isCourse,
  handleAddOrderLine,
  handleEditOrderLine,
  handleRemoveOrderLine,
  handleAddOrEditFreight,
  handleOrderFieldChanged,
  handleSimpleOrderLineFieldChanged,
  downloadOrderConfirmation,
  downloadDeliveryNotification,
  downloadDeliveryNote,
  handleEmailOrderConfirmation,
  handlePrintDeliveryAddress,
  useSeparateReceiverCompany,
  setUseSeparateReceiverCompany,
  useSeparateInvoiceReference,
  setUseSeparateInvoiceReference,
  useTempReference,
  handleSetUseTempReference,
  handleSaveOrSendOrder,
  handleClose,
  hasOrderChanged,
  hasExcludeFromStatsChanged,
  highlightAutoChange,
}) {
  const [documentLoading, setDocumentLoading] = useState(false);
  const apiRef = useGridApiRef();
  const [documentAnchorEl, setDocumentAnchorEl] = useState(null);
  const documentOpen = Boolean(documentAnchorEl);

  const localizedTextsMap = dataGridLang();
  const theme = useTheme();
  const isLargeUp = useMediaQuery(theme.breakpoints.up("lg"));

  function getDotMenuActions(orderLine) {
    var canUpdate = canUpdateOrderLine(orderLine, order.isPrepay ?? false, order.paymentStatus);

    return [
      ...(isFreightPartType(orderLine.part?.partType)
        ? [] // We edit freight using another button
        : [
            {
              icon: <Edit />,
              text: Translate.get("EditLabel"),
              disabled: !canUpdate || isServiceAgreement || isEPortLicense,
              onClick: () => handleEditOrderLine(orderLine.orderLineId),
            },
          ]),
      {
        icon: orderLine.excludeFromStats ? <Visibility /> : <VisibilityOff />,
        text: orderLine.excludeFromStats
          ? Translate.get("IncludeOrderLineInStats")
          : Translate.get("ExcludeOrderLineFromStats"),
        disabled: !order.creditId,
        onClick: () =>
          handleSimpleOrderLineFieldChanged(orderLine.orderLineId, "excludeFromStats", !orderLine.excludeFromStats),
      },
      {
        icon: <Delete />,
        text: Translate.get("Delete"),
        disabled: !canUpdate || isServiceAgreement || isEPortLicense || isCourse,
        onClick: () => handleRemoveOrderLine(orderLine.orderLineId),
      },
    ];
  }

  const columns = [
    {
      field: "partNo",
      flex: 0.3,
      headerName: Translate.get("PartNoLabel"),
      renderHeader: () => <IconAndLabelColumnHeader icon={Numbers} label={Translate.get("PartNoLabel")} />,
      valueGetter: (value, row, column, apiRef) => row.part?.partNo ?? "",
    },
    {
      field: "partId",
      flex: 1,
      headerName: Translate.get("Product"),
      renderHeader: () => <IconAndLabelColumnHeader icon={ShoppingBasketIcon} label={Translate.get("Product")} />,
      valueGetter: (value, row, columm, apiRef) => row.part?.description ?? "",
    },
    {
      field: "qty",
      flex: 0.3,
      headerName: Translate.get("Quantity"),
      renderHeader: () => <IconAndLabelColumnHeader icon={ScatterPlotIcon} label={Translate.get("Quantity")} />,
    },
    {
      field: "discountedPcsPrice",
      flex: 0.4,
      headerName: Translate.get("PricePerUnit"),
      renderHeader: () => <IconAndLabelColumnHeader icon={AttachMoney} label={Translate.get("PricePerUnit")} />,
      valueGetter: (value, row, column, apiRef) => (row.discountedPcsPrice ? row.discountedPcsPrice.toFixed(2) : ""),
    },
    {
      field: "discount",
      flex: 0.4,
      headerName: Translate.get("Discount"),
      renderHeader: () => <IconAndLabelColumnHeader icon={Discount} label={Translate.get("Discount")} />,
      valueGetter: (value, row, column, apiRef) => (row.discount ? `${(row.discount * 100).toFixed(2)}%` : ""),
    },
    {
      field: "price",
      flex: 0.4,
      headerName: Translate.get("Price"),
      renderHeader: () => <IconAndLabelColumnHeader icon={Money} label={Translate.get("Price")} />,
      valueGetter: (value, row, column, apiRef) => (row.price ? row.price.toFixed(2) : ""),
    },
    {
      field: "vat",
      flex: 0.2,
      headerName: Translate.get("VATText"),
      renderHeader: () => <IconAndLabelColumnHeader icon={AccountBalance} label={Translate.get("VATText")} />,
      valueGetter: (value, row, column, apiRef) =>
        row.vat ? `${(row.vat * 100) % 1 > 0 ? (row.vat * 100).toFixed(1) : row.vat * 100}%` : "",
    },
    {
      field: "dotMenu",
      flex: 0.2,
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      headerClassName: "hideSeparator",
      align: "right",
      hide: !getDotMenuActions,
      hideable: false,
      renderCell: (params) => getDotMenuActions && <DotMenu content={getDotMenuActions(params.row)} horizontal />,
    },
  ];

  function isEmptyTempAddress() {
    return (
      !order.tempAddress ||
      (!order.tempAddress.street1 &&
        !order.tempAddress.street2 &&
        !order.tempAddress.cityCode &&
        !order.tempAddress.city)
    );
  }

  function getDialogTitle() {
    if (!order.orderId) {
      return Translate.get("CreateOrder");
    }

    let title = `${Translate.get("Order")} ${order.orderNo}`;
    if (!order.isPrepay) {
      title += ` (${Translate.getOrderStatus(order.status).toLowerCase()})`;
      return title;
    } else {
      const titleOrderStatus = Translate.getOrderStatus(order.status).toLowerCase();
      const titlePrepayStatus = `${Translate.get("PrePayment").toLowerCase()}: ${Translate.getPaymentStatus(
        order.paymentStatus,
      ).toLowerCase()}`;
      title += ` (${titleOrderStatus} - ${titlePrepayStatus})`;
      return title;
    }
  }

  return (
    <>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent sx={{ width: { lg: "1160px", xl: "1460px" } }}>
        <Grid container spacing={4}>
          <Grid container rowSpacing={2} columnSpacing={1} size={12}>
            <Grid size={12}>
              <Grid container spacing={2} direction="row" justifyContent="space-between" alignItems="flex-end">
                <Grid>
                  <h4>{Translate.get("Company")}</h4>
                </Grid>
                <Grid direction="row">
                  <FormControlLabel
                    sx={{ marginBottom: "-10px" }}
                    control={
                      <Checkbox
                        onChange={(event) => {
                          setUseSeparateReceiverCompany(event.target.checked);

                          // When we uncheck we will replace the actorIds of the
                          // receiver with those of the order's company
                          const resetToOrderCompany = !event.target.checked;
                          handleResetReceiverCompany(resetToOrderCompany);
                        }}
                        checked={useSeparateReceiverCompany}
                        disabled={!order?.canUpdate}
                      />
                    }
                    label={Translate.get("UseSeparateReceiverCompany")}
                  />
                  <FormControlLabel
                    sx={{ marginBottom: "-10px" }}
                    control={
                      <Checkbox
                        onChange={(event) => {
                          setUseSeparateInvoiceReference(event.target.checked);

                          // When we uncheck we will replace the actorIds of the
                          // receiver with those of the order's company
                          const resetToOrderRef = !event.target.checked;
                          handleResetInvoiceReference(resetToOrderRef);
                        }}
                        checked={useSeparateInvoiceReference}
                        disabled={!order?.canUpdate}
                      />
                    }
                    label={Translate.get("UseSeparateInvoiceRef")}
                  />
                  <FormControlLabel
                    sx={{ marginBottom: "-10px" }}
                    control={
                      <Checkbox
                        onChange={(event) => {
                          handleSetUseTempReference(event.target.checked);
                        }}
                        checked={useTempReference}
                        disabled={!order?.canUpdate}
                      />
                    }
                    label={Translate.get("TempReference")}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container columnSpacing={1} rowSpacing={2} size={12}>
              <Grid size={{ xs: 12, md: 6 }}>
                <AutocompleteSearchField
                  disabled={!canChangeCompany || order.orderId > 0}
                  label={Translate.get("Company")}
                  value={company ?? ""}
                  onValueChange={async (selectedCompany) => await handleCompanySelected(selectedCompany)}
                  getOptionLabel={(option) => option?.actorName ?? ""}
                  endAdornment={
                    company &&
                    company.observationNote && (
                      <Tooltip title={company.observationNote} className="endAdornmentObservationNote" arrow>
                        <Error />
                      </Tooltip>
                    )
                  }
                  renderOption={(option) => (
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <ActorAutocompleteOption
                        actorName={option?.actorName}
                        externalActorNo={
                          option?.actorId.toString() + (option?.externalActorNo ? ` (${option?.externalActorNo})` : "")
                        }
                        addressStreet={option?.addressStreet}
                        addressZipCode={option?.addressZipCode}
                        addressCity={option?.addressCity}
                      />
                      {(option.externalDataId || option.observationNote) && (
                        <Stack
                          direction="column"
                          justifyContent={
                            option.externalDataId && option.observationNote
                              ? "space-between"
                              : option.externalDataId
                                ? "flex-start"
                                : "flex-end"
                          }
                        >
                          {option.externalDataId && (
                            <Box display="flex" width="80px" gap={1}>
                              <Computer />
                              <Box>{option.externalDataId ?? ""}</Box>
                            </Box>
                          )}
                          {option.observationNote && (
                            <Tooltip title={option.observationNote} arrow>
                              <Error sx={{ color: "red" }} />
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                    </Box>
                  )}
                  keyPropName="actorId"
                  requireSelection={true}
                  search={companySearch}
                  createNewValue={(text) => ({
                    actorName: text,
                  })}
                  textFieldProps={{
                    required: true,
                    error: !!company && isExternalDataIdRequired() && !company?.externalDataId,
                    helperText:
                      !!company && isExternalDataIdRequired() && !company?.externalDataId
                        ? Translate.get("CompanyHasNoDataId")
                        : "",
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel id="contact-select-label">{Translate.get("YourReference")}</InputLabel>
                  <Select
                    required
                    disabled={!order.canUpdate || isServiceAgreement || isEPortLicense}
                    labelId="contact-select-label"
                    id="contact-select"
                    value={order?.actorRefId ?? ""}
                    label={Translate.get("YourReference")}
                    onChange={handleContactSelected}
                  >
                    {contacts &&
                      contacts.map((contact) => (
                        <MenuItem key={contact.actorId} value={contact.actorId}>
                          {contact.actorName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {useSeparateReceiverCompany && (
              <Grid container columnSpacing={1} rowSpacing={2} size={12}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <AutocompleteSearchField
                    disabled={!order.canUpdate}
                    label={Translate.get("ReceiverCompany")}
                    value={receiverCompany ?? ""}
                    onValueChange={handleReceiverCompanySelected}
                    getOptionLabel={(option) => option?.actorName ?? ""}
                    renderOption={(option) => (
                      <ActorAutocompleteOption
                        actorName={option?.actorName}
                        externalActorNo={
                          option?.actorId.toString() + (option?.externalActorNo ? ` (${option?.externalActorNo})` : "")
                        }
                        addressStreet={option?.addressStreet}
                        addressZipCode={option?.addressZipCode}
                        addressCity={option?.addressCity}
                      />
                    )}
                    keyPropName="actorId"
                    requireSelection={true}
                    search={companySearch}
                    createNewValue={(text) => ({
                      actorName: text,
                    })}
                    textFieldProps={{
                      error: !!receiverCompany && isExternalDataIdRequired() && !receiverCompany?.externalDataId,
                      helperText:
                        !!receiverCompany && isExternalDataIdRequired() && !receiverCompany?.externalDataId
                          ? Translate.get("CompanyHasNoDataId")
                          : "",
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="receiver-contact-select-label">{Translate.get("ReceiverRef")}</InputLabel>
                    <Select
                      disabled={!order.canUpdate || isServiceAgreement || isEPortLicense || useTempReference}
                      labelId="receiver-contact-select-label"
                      id="receiver-contact-select"
                      value={order?.receiverActorRefId ?? ""}
                      label={Translate.get("ReceiverRef")}
                      onChange={(event) => handleOrderFieldChanged("receiverActorRefId", event.target.value)}
                    >
                      {receiverContacts &&
                        receiverContacts.map((contact) => (
                          <MenuItem key={contact.actorId} value={contact.actorId}>
                            {contact.actorName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {useTempReference && (
              <Grid container columnSpacing={1} rowSpacing={2} size={12}>
                <Grid size={{ xs: 12, md: 6 }}>{/* Empt item on purpose */}</Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    disabled={!order.canUpdate}
                    fullWidth
                    value={order?.tempReferenceText ?? ""}
                    InputProps={{ spellCheck: false }}
                    onChange={(event) => handleOrderFieldChanged("tempReferenceText", event.target.value)}
                    label={Translate.get("TempReference")}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            )}

            {useSeparateInvoiceReference && (
              <Grid container columnSpacing={1} rowSpacing={2} size={12}>
                <Grid size={{ xs: 12, md: 6 }}>{/* Empty item on purpose */}</Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="invoice-contact-select-label">{Translate.get("InvoiceRef")}</InputLabel>
                    <Select
                      required
                      disabled={!order.canUpdate}
                      labelId="invoice-contact-select-label"
                      id="invoice-contact-select"
                      value={order?.invoiceActorRefId ?? ""}
                      label={Translate.get("InvoiceRef")}
                      onChange={(event) => handleOrderFieldChanged("invoiceActorRefId", event.target.value)}
                    >
                      {contacts &&
                        contacts.map((contact) => (
                          <MenuItem key={contact.actorId} value={contact.actorId}>
                            {contact.actorName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            <Grid container columnSpacing={1} rowSpacing={2} size={12}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="del-address-select-label">{Translate.getActorAddressType(4)}</InputLabel>
                  <Select
                    sx={{ height: "130px" }}
                    multiline
                    rows={4}
                    labelId="del-address-select-label"
                    id="del-address-select"
                    value={order.deliveryAddressId ?? ""}
                    className={highlightAutoChange.deliveryAddressId ? "field-change-highlight" : null}
                    label={Translate.getActorAddressType(4)}
                    onChange={(event) => handleDeliveryAddressSelected(event.target.value)}
                    disabled={!order.canUpdate}
                  >
                    {(useSeparateReceiverCompany ? receiverCompanyAddresses : addresses) &&
                      (useSeparateReceiverCompany ? receiverCompanyAddresses : addresses).map((address) => (
                        <MenuItem divider key={address.actorAddressId} value={address.actorAddressId}>
                          <Grid container whiteSpace="pre" sx={{ width: "100%" }}>
                            <Grid size={9}>
                              {address.actorAddressStreet1?.trim() ? address.actorAddressStreet1 : " "}
                            </Grid>
                            <Grid size={3}>
                              <span
                                style={{
                                  backgroundColor: "#EDEFF1",
                                  padding: "3px",
                                  borderRadius: "3px",
                                  fontSize: "smaller",
                                }}
                              >
                                {Translate.getActorAddressType(address.actorAddressType)}
                              </span>
                            </Grid>
                            <Grid size={12}>
                              {address.actorAddressStreet2?.trim() ? address.actorAddressStreet2 : " "}
                            </Grid>
                            <Grid size={12}>
                              {`${address.actorAddressZipCode?.trim() ? address.actorAddressZipCode : " "} ${
                                address.actorAddressCity?.trim() ? address.actorAddressCity : " "
                              }`}
                            </Grid>
                            {address.actorAddressCountry?.trim() && (
                              <Grid size={12}>
                                {address.actorAddressCountry?.trim() ? address.actorAddressCountry : " "}
                              </Grid>
                            )}
                          </Grid>
                        </MenuItem>
                      ))}
                    <MenuItem value={-1}>
                      <Grid container whiteSpace="pre" sx={{ width: "100%" }}>
                        <Grid container size={9}>
                          {isEmptyTempAddress() ? (
                            <Grid size={12}>
                              <Box height="96px" display="flex" alignItems="center">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {Translate.getActorAddressType(5)}
                                </Typography>
                              </Box>
                            </Grid>
                          ) : (
                            <>
                              <Grid size={12}>
                                {order.tempAddress?.street1?.trim() ? order.tempAddress?.street1 : " "}
                              </Grid>
                              <Grid size={12}>
                                {order.tempAddress?.street2?.trim() ? order.tempAddress?.street2 : " "}
                              </Grid>
                              <Grid size={12}>
                                {`${order.tempAddress?.cityCode?.trim() ? order.tempAddress?.cityCode : " "} ${
                                  order.tempAddress?.city?.trim() ? order.tempAddress?.city : " "
                                }`}
                              </Grid>
                              {order.tempAddress?.country?.trim() && (
                                <Grid size={12}>
                                  {order.tempAddress?.country?.trim() ? order.tempAddress.country : " "}
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                        <Grid container size={3}>
                          <Grid size={12}>
                            <span
                              style={{
                                backgroundColor: "#EDEFF1",
                                padding: "3px",
                                borderRadius: "3px",
                                fontSize: "smaller",
                              }}
                            >
                              {Translate.getActorAddressType(5)}
                            </span>
                          </Grid>
                          {(!isEmptyTempAddress() || order.deliveryAddressId === -1) && (
                            <Grid justifyContent="center" className="showWhenMenuOpen" size={12}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleDeliveryAddressSelected(-1)}
                              >
                                {Translate.get("EditLabel")}
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="inv-address-select-label">{Translate.getActorAddressType(1)}</InputLabel>
                  <Select
                    sx={{ height: "130px" }}
                    multiline
                    rows={4}
                    labelId="inv-address-select-label"
                    id="inv-address-select"
                    value={order.invoiceAddressId ?? ""}
                    label={Translate.getActorAddressType(1)}
                    onChange={(event) => handleOrderFieldChanged("invoiceAddressId", event.target.value)}
                    disabled={!order.canUpdate}
                  >
                    {addresses &&
                      addresses.map((address) => (
                        <MenuItem divider key={address.actorAddressId} value={address.actorAddressId}>
                          <Grid container whiteSpace="pre" sx={{ width: "100%" }}>
                            <Grid size={9}>
                              {address.actorAddressStreet1?.trim() ? address.actorAddressStreet1 : " "}
                            </Grid>
                            <Grid size={3}>
                              <span
                                style={{
                                  backgroundColor: "#EDEFF1",
                                  padding: "3px",
                                  borderRadius: "3px",
                                  fontSize: "smaller",
                                }}
                              >
                                {Translate.getActorAddressType(address.actorAddressType)}
                              </span>
                            </Grid>
                            <Grid size={12}>
                              {address.actorAddressStreet2?.trim() ? address.actorAddressStreet2 : " "}
                            </Grid>
                            <Grid size={12}>
                              {`${address.actorAddressZipCode?.trim() ? address.actorAddressZipCode : " "} ${
                                address.actorAddressCity?.trim() ? address.actorAddressCity : " "
                              }`}
                            </Grid>
                            {address.actorAddressCountry?.trim() && (
                              <Grid size={12}>
                                {address.actorAddressCountry?.trim() ? address.actorAddressCountry : " "}
                              </Grid>
                            )}
                          </Grid>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid container columnSpacing={1} rowSpacing={2} size={12}>
            <Grid size={12}>
              <h4>{Translate.get("OrderDetails")}</h4>
            </Grid>
            <Grid container spacing={2} size={{ xs: 12, md: 4 }}>
              <Grid size={12}>
                <TextField
                  disabled={!order.canUpdate}
                  // Can't require, because some clients need an order confirmation before they can provide order number
                  // required={company?.defaultCustomerOrderNoRequired}
                  fullWidth
                  value={order?.customerOrderNo ?? ""}
                  className={highlightAutoChange.customerOrderNo ? "field-change-highlight" : null}
                  InputProps={{
                    spellCheck: false,
                    endAdornment: company?.defaultCustomerOrderNoRequired && !order?.customerOrderNo && (
                      <Tooltip
                        arrow
                        className="warning nobackground"
                        title={Translate.get("DefaultCustomerOrderNoRequired")}
                      >
                        <Warning />
                      </Tooltip>
                    ),
                  }}
                  onChange={(event) => handleOrderFieldChanged("customerOrderNo", event.target.value)}
                  label={Translate.get("YourOrderNo")}
                  inputProps={{
                    maxLength: 30,
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  disabled={!order.canUpdate}
                  fullWidth
                  value={order?.goodsMarking ?? ""}
                  className={highlightAutoChange.goodsMarking ? "field-change-highlight" : null}
                  InputProps={{ spellCheck: false }}
                  onChange={(event) => handleOrderFieldChanged("goodsMarking", event.target.value)}
                  label={Translate.get("GoodsMarking")}
                  inputProps={{ maxLength: 30 }}
                />
              </Grid>
              <Grid size={12}>
                <DatePicker
                  displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                  disabled={!order.canUpdate}
                  mask={getCurrentDateMask()}
                  format={getCurrentInputFormat()}
                  sx={{ width: "100%" }}
                  value={order.pickDate ? new Date(order.pickDate) : null}
                  onChange={(newDate) => {
                    if (isValidDate(newDate)) {
                      handleOrderFieldChanged("pickDate", toSameDateTimeUTC(newDate));
                    }
                  }}
                  label={Translate.get("PickDate")}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} size={{ xs: 12, md: 4 }}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="delivery-agreement-label">{Translate.get("DeliveryTerms")}</InputLabel>
                  <Select
                    label={Translate.get("DeliveryTerms")}
                    labelId="delivery-agreement-label"
                    disabled={!order.canUpdate}
                    value={order.deliveryAgreement ?? ""}
                    onChange={(event) => handleOrderFieldChanged("deliveryAgreement", event.target.value)}
                    endAdornment={
                      order.deliveryAgreement && (
                        <InputAdornment sx={{ marginRight: "10px" }} position="end">
                          <IconButton
                            disabled={!order.canUpdate}
                            onClick={() => handleOrderFieldChanged("deliveryAgreement", null)}
                          >
                            <Clear></Clear>
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {deliveryAgreements.map((deliveryAgreement) => (
                      <MenuItem key={deliveryAgreement.ePortCodeId} value={deliveryAgreement.ePortCodeId}>
                        {deliveryAgreement.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="mode-of-transport-label">{Translate.get("ModeOfTransport")}</InputLabel>
                  <Select
                    label={Translate.get("ModeOfTransport")}
                    labelId="mode-of-transport-label"
                    disabled={!order.canUpdate}
                    value={order.modeOfTransport ?? ""}
                    onChange={(event) => handleOrderFieldChanged("modeOfTransport", event.target.value)}
                    endAdornment={
                      order.modeOfTransport && (
                        <InputAdornment sx={{ marginRight: "10px" }} position="end">
                          <IconButton
                            disabled={!order.canUpdate}
                            onClick={() => handleOrderFieldChanged("modeOfTransport", null)}
                          >
                            <Clear></Clear>
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {modesOfTransport.map((modeOfTransport) => (
                      <MenuItem key={modeOfTransport.ePortCodeId} value={modeOfTransport.ePortCodeId}>
                        {modeOfTransport.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="payment-agreement-label">{Translate.get("PaymentTerm")}</InputLabel>
                  <Select
                    label={Translate.get("PaymentTerm")}
                    labelId="payment-agreement-label"
                    disabled={!order.canUpdate}
                    value={order.paymentAgreement ?? ""}
                    onChange={(event) => handleOrderFieldChanged("paymentAgreement", event.target.value)}
                    endAdornment={
                      order.paymentAgreement && (
                        <InputAdornment sx={{ marginRight: "10px" }} position="end">
                          <IconButton
                            disabled={!order.canUpdate}
                            onClick={() => handleOrderFieldChanged("paymentAgreement", null)}
                          >
                            <Clear></Clear>
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {paymentAgreements.map((paymentAgreement) => (
                      <MenuItem
                        key={paymentAgreement.ePortCodeId}
                        value={paymentAgreement.ePortCodeId}
                        disabled={
                          !order.isPrepay &&
                          order.status > orderStatus.ordered &&
                          order.status != orderStatus.waitingForCourseInvoice &&
                          paymentAgreement.isPrepayPaymentAgreement
                        }
                      >
                        {paymentAgreement.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} size={{ xs: 12, md: 4 }}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel id="currencies-label">{Translate.get("CurrencyLabel")}</InputLabel>
                  <Select
                    label={Translate.get("CurrencyLabel")}
                    labelId="currency-label"
                    disabled={!order.canUpdate || orderLines.length > 0}
                    value={order.currency ?? ""}
                    onChange={(event) => handleOrderFieldChanged("currency", event.target.value)}
                  >
                    {currencies.map((currency) => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {`${currency.code} ${currency.description} (${formatMoney(
                          0,
                          currency.cultureName,
                          currency.code,
                        )
                          .replace("0", "")
                          .replace(".", "")
                          .replace(",", "")
                          .trim()})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  disabled={!order.canUpdate}
                  fullWidth
                  multiline
                  rows={4}
                  value={order?.extraNotification ?? ""}
                  onChange={(event) => handleOrderFieldChanged("extraNotification", event.target.value)}
                  label={Translate.get("ExtraNotificationLabel")}
                  inputProps={{ maxLength: 1048 }}
                />
              </Grid>
            </Grid>
          </Grid>

          {order.orderId > 0 && (
            <Grid container columnSpacing={1} rowSpacing={2} size={12}>
              <Grid size={12}>
                <h4>{Translate.get("PaymentDetails")}</h4>
              </Grid>
              <Grid container spacing={2} size={{ xs: 12, md: order?.creditId ? 4 : 6 }}>
                <Grid size={12}>
                  <TextField
                    disabled
                    fullWidth
                    value={order?.invoicePrice ?? ""}
                    label={Translate.get("InvoicePrice")}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField disabled fullWidth value={order?.invoiceNo ?? ""} label={Translate.get("InvoiceNumber")} />
                </Grid>
                <Grid size={12}>
                  <DatePicker
                    displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                    disabled
                    label={Translate.get("InvoiceDate")}
                    mask={getCurrentDateMask()}
                    format={getCurrentInputFormat()}
                    value={order.invoiceDate ? new Date(order.invoiceDate) : null}
                    sx={{ width: "100%" }}
                    onChange={() => {
                      /* Noop just to avoid console errors */
                    }}
                    slotProps={{
                      textField: {
                        helperText: order.scheduledInvoiceDate
                          ? `${Translate.getOrderStatus(orderStatus.scheduledForInvoice)}: ${formatDate(
                              order.scheduledInvoiceDate,
                            )}`
                          : null,
                      },
                      fullWidth: true,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} size={{ xs: 12, md: order?.creditId ? 4 : 6 }}>
                <Grid size={12}>
                  <TextField disabled fullWidth value={order?.paidPrice ?? ""} label={Translate.get("PaidPrice")} />
                </Grid>
                <Grid size={12}>
                  <DatePicker
                    displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                    disabled
                    label={Translate.get("PaymentDate")}
                    mask={getCurrentDateMask()}
                    format={getCurrentInputFormat()}
                    sx={{ width: "100%" }}
                    value={order.paymentDate ? new Date(order.paymentDate) : null}
                    onChange={() => {
                      /* Noop just to avoid console errors */
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid size={12}>
                  <DatePicker
                    displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                    label={Translate.get("DueDate")}
                    disabled
                    mask={getCurrentDateMask()}
                    format={getCurrentInputFormat()}
                    sx={{ width: "100%" }}
                    value={order.lastPaymentDate ? new Date(order.lastPaymentDate) : null}
                    onChange={() => {
                      /* Noop just to avoid console errors */
                    }}
                    slotProps={{
                      textField: {
                        // Just for alignment with other grid column
                        helperText: order.scheduledInvoiceDate ? " " : null,
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              {order?.creditId && (
                <Grid container spacing={2} size={{ xs: 12, md: 4 }}>
                  <Grid size={12}>
                    <TextField
                      disabled
                      fullWidth
                      value={order?.creditAmount ?? ""}
                      label={Translate.get("CreditNoteAmount")}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField disabled fullWidth value={order?.creditId ?? ""} label={Translate.get("CreditNote")} />
                  </Grid>
                  <Grid size={12}>
                    <DatePicker
                      displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                      label={Translate.get("CreditNoteDate")}
                      disabled
                      mask={getCurrentDateMask()}
                      format={getCurrentInputFormat()}
                      sx={{ width: "100%" }}
                      value={order.creditDate ? new Date(order.creditDate) : null}
                      onChange={() => {
                        /* Noop just to avoid console errors */
                      }}
                      slotProps={{
                        textField: {
                          // Just for alignment with other grid column
                          helperText: order.scheduledInvoiceDate ? " " : null,
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}

          <Grid container spacing={1} size={12}>
            <Grid size={12}>
              <DataGrid
                showToolbar
                apiRef={apiRef}
                className={"dgDynIconText dgSmall" + (orderLines.length === 0 ? " hideOverlayWrapper" : "")}
                rows={orderLines.filter(
                  (ol) => ol.status === undefined || ol.status === null || ol.status > orderStatus.canceled,
                )}
                columns={columns}
                disableRowSelectionOnClick
                getRowId={(row) => `${row.partId}_${row.orderLineId}`}
                autoHeight={true}
                slots={{
                  toolbar: () => ToolBar({ showExport: true, apiRef }),
                  noRowsOverlay: () => <div></div>,
                }}
                slotProps={{
                  columnsPanel: { className: "customColumnPanel" },
                  filterPanel: { className: "customfilterPanel" },
                  panel: { className: "customPanel" },
                }}
                localeText={localizedTextsMap}
                disableColumnMenu
                disableDensitySelector={true}
                density="comfortable"
                hideFooter={true}
              />
            </Grid>
            <Grid marginLeft={2} size={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  disabled={!order.canUpdate || !order.actorId || isServiceAgreement || isEPortLicense || isCourse}
                  onClick={handleAddOrderLine}
                  startIcon={<Add />}
                >
                  {Translate.get("PartLabel")}
                </Button>
                <Button
                  disabled={
                    !order.canUpdate ||
                    isServiceAgreement ||
                    isEPortLicense ||
                    isCourse ||
                    orderLines.length === 0 ||
                    !orderLines
                      .filter((ol) => ol.status > 0)
                      .some((ol) => !!ol.part && !isFreightPartType(ol.part.partType))
                  }
                  onClick={handleAddOrEditFreight}
                  startIcon={
                    orderLines.filter((ol) => ol.status > 0).some((ol) => isFreightPartType(ol.part?.partType)) ? (
                      <Edit />
                    ) : (
                      <Add />
                    )
                  }
                >
                  {Translate.get("Freight")}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MenuToggleButton
          label={Translate.get("Priority")}
          states={[priority.high, priority.normal, priority.onHold].map((prio) => ({
            value: prio,
            label: Translate.getPriority(prio),
            icon: getIcon(prio),
            onClick: () => handleOrderFieldChanged("priority", prio),
          }))}
          value={order.priority ?? priority.normal}
        />
        <Box>
          <Button
            loading={documentLoading}
            startIcon={<MoreHoriz />}
            variant="outlined"
            id="basic-button"
            aria-controls={documentOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={documentOpen ? "true" : undefined}
            onClick={(e) => setDocumentAnchorEl(e.currentTarget)}
          >
            {Translate.get("Document")}
          </Button>
          <Menu
            open={!!documentOpen}
            anchorEl={documentAnchorEl}
            onClose={() => setDocumentAnchorEl(null)}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={async () => {
                setDocumentAnchorEl(null);
                setDocumentLoading(true);
                await downloadOrderConfirmation();
                setDocumentLoading(false);
              }}
            >
              <ListItemIcon>
                <PictureAsPdfIcon />
              </ListItemIcon>
              <ListItemText>{Translate.get("OrderConfirmation")}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setDocumentAnchorEl(null);
                setDocumentLoading(true);
                await downloadDeliveryNotification();
                setDocumentLoading(false);
              }}
            >
              <ListItemIcon>
                <PictureAsPdfIcon />
              </ListItemIcon>
              <ListItemText>{Translate.get("DeliveryNotification")}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setDocumentAnchorEl(null);
                setDocumentLoading(true);
                await downloadDeliveryNote();
                setDocumentLoading(false);
              }}
            >
              <ListItemIcon>
                <PictureAsPdfIcon />
              </ListItemIcon>
              <ListItemText>{Translate.get("DeliveryNoteLabel")}</ListItemText>
            </MenuItem>

            <MenuItem
              disabled={
                !company || !order.actorId || !order.orderId || (company.isMAToyotaClient && company.isToyotaInvoice)
              }
              onClick={() => {
                setDocumentAnchorEl(null);
                handleEmailOrderConfirmation();
              }}
            >
              <ListItemIcon>
                {orderCommunications &&
                orderCommunications.length > 0 &&
                orderCommunications.some(
                  (oc) => oc.documentType === documentType.orderConfirmation && oc.media === mediaType.email,
                ) ? (
                  <MarkEmailRead />
                ) : (
                  <Email />
                )}
              </ListItemIcon>
              <ListItemText>{Translate.get("OrderConfirmation")}</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                setDocumentAnchorEl(null);
                handlePrintDeliveryAddress();
              }}
            >
              <ListItemIcon>
                <Print />
              </ListItemIcon>
              <ListItemText>{Translate.getActorAddressType(4)}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
        <Button onClick={handleClose}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner
          variant="contained"
          onClick={handleSaveOrSendOrder}
          disabled={
            !company ||
            (isExternalDataIdRequired() && !company.externalDataId) ||
            // hasExcludeFromStatsChanged has separate handling since this can
            // only be changed after an order has been invoice and credited
            (hasExcludeFromStatsChanged ? false : !order.canUpdate) ||
            !hasOrderChanged ||
            !orderLines ||
            orderLines.length === 0 ||
            !order.actorRefId ||
            (useSeparateInvoiceReference && !order.invoiceActorRefId)
          }
        >
          {order.orderId ? Translate.get("Save") : Translate.get("CreateOrder")}
        </ButtonWithSpinner>
      </DialogActions>
    </>
  );
}
